import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAPI } from '../../utils/api';
import { FaPlus, FaTrash, FaEdit, FaUserPlus, FaKey, FaSearch, FaFileDownload } from 'react-icons/fa';
import { PasswordStrengthIndicator, validatePassword } from '../../utils/passwordValidation';
import * as XLSX from 'xlsx';
import { showSuccess, showError } from '../../utils/toastUtils';
import { useConfirm } from '../../hooks/useConfirm';
import ConfirmDialog from '../../components/common/ConfirmDialog';

export default function UserManagement() {
    const { users, loadUsers } = useAuth();
    const { confirm, ConfirmDialog: confirmDialogProps } = useConfirm();
    const [showAddForm, setShowAddForm] = useState(false);
    const [newUser, setNewUser] = useState({
        name: '', email: '', password: '', role: 'student',
        phone: '', birthday: '', address: '', age: '',
        grade: '', index_number: '', year: '', medium: 'Sinhala',
        student_type: 'physical' // Default
    });

    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editUserId, setEditUserId] = useState(null);

    // Security Pin State
    const [showPinModal, setShowPinModal] = useState(true);
    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState('');

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const usersPerPage = 10;

    const handleChange = (e) => {
        setNewUser({ ...newUser, [e.target.name]: e.target.value });
    };

    // Filter Users
    const filteredUsers = users.filter(user =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.index_number && user.index_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (user.role && user.role.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Calculate Pagination
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    const changePage = (pageNumber) => setCurrentPage(pageNumber);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate password for new users
        if (!isEditing && newUser.password) {
            const validation = validatePassword(newUser.password);
            if (!validation.isValid) {
                return showError('Password does not meet minimum requirements');
            }
        }

        try {
            const url = isEditing ? '/admin/update_user.php' : '/admin/add_user.php';
            const body = isEditing ? { ...newUser, id: editUserId } : newUser;

            await fetchAPI(url, { method: 'POST', body: JSON.stringify(body) });
            showSuccess(isEditing ? 'User updated successfully!' : 'User added successfully!');
            setShowAddForm(false);
            resetForm();
            loadUsers();
        } catch (err) {
            showError(err.message);
        }
    };

    const resetForm = () => {
        setNewUser({
            name: '', email: '', password: '', role: 'student',
            phone: '', birthday: '', address: '', age: '',
            grade: '', index_number: '', year: '',
            student_type: 'physical'
        });
        setIsEditing(false);
        setEditUserId(null);
    };

    const handleEdit = (user) => {
        setNewUser({
            ...user,
            password: '' // Don't show password
        });
        setEditUserId(user.id);
        setIsEditing(true);
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        const confirmed = await confirm({
            title: 'Delete User',
            message: 'Are you sure you want to delete this user? This action cannot be undone.',
            confirmText: 'Delete User',
            cancelText: 'Cancel',
            danger: true,
            icon: 'trash'
        });

        if (confirmed) {
            try {
                await fetchAPI('/admin/delete_user.php', { method: 'POST', body: JSON.stringify({ id }) });
                showSuccess('User deleted successfully');
                loadUsers();
            } catch (err) {
                showError(err.message);
            }
        }
    };

    const openPasswordModal = (user) => {
        setSelectedUserForPassword(user);
        setNewPassword('');
        setShowPasswordModal(true);
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (!selectedUserForPassword) return;

        const validation = validatePassword(newPassword);
        if (!validation.isValid) {
            return showError('Password does not meet minimum requirements');
        }

        try {
            await fetchAPI('/admin/admin_update_password.php', {
                method: 'POST',
                body: JSON.stringify({
                    user_id: selectedUserForPassword.id,
                    new_password: newPassword
                })
            });
            showSuccess('Password updated successfully!');
            setShowPasswordModal(false);
        } catch (err) {
            showError(err.message);
        }
    };

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        try {
            await fetchAPI('/admin/validate_pin.php', {
                method: 'POST',
                body: JSON.stringify({ pin })
            });
            setShowPinModal(false); // Unlock page
        } catch (err) {
            setPinError('Invalid PIN');
            setPin('');
        }
    };

    // Export to Excel
    const handleExportToExcel = async () => {
        try {
            // Show loading state
            const exportButton = document.getElementById('export-excel-btn');
            if (exportButton) {
                exportButton.disabled = true;
                exportButton.textContent = 'Exporting...';
            }

            // Fetch all necessary data
            const [resultsData, paymentsData] = await Promise.all([
                fetchAPI('/admin/get_all_results.php').catch(() => ({ results: [] })),
                fetchAPI('/admin/get_all_payments.php').catch(() => ({ payments: [] }))
            ]);

            // Prepare Users Data
            const usersSheet = users.map(user => ({
                'ID': user.id,
                'Name': user.name,
                'Email': user.email,
                'Role': user.role?.toUpperCase(),
                'Phone': user.phone || '-',
                'Birthday': user.birthday || '-',
                'Age': user.age || '-',
                'Address': user.address || '-',
                'Index Number': user.index_number || '-',
                'Grade': user.grade || '-',
                'Year': user.year || '-',
                'Student Type': user.student_type || '-',
                'Created At': user.created_at || '-'
            }));

            // Prepare Results Data
            const resultsSheet = (resultsData.results || []).map(result => ({
                'Student Name': result.student_name,
                'Index Number': result.index_number,
                'Grade': result.grade,
                'Exam Name': result.exam_name,
                'Exam Date': result.exam_date,
                'Marks Obtained': result.marks_obtained,
                'Total Marks': result.total_marks,
                'Percentage': result.percentage + '%',
                'Status': result.status,
                'Rank': result.rank || '-',
                'Subject': result.subject || '-'
            }));

            // Prepare Payments Data
            const paymentsSheet = (paymentsData.payments || []).map(payment => ({
                'Student Name': payment.student_name,
                'Class Name': payment.class_name,
                'Grade': payment.grade,
                'Month': payment.month,
                'Amount (LKR)': payment.amount,
                'Reference No': payment.reference_no,
                'Status': payment.status,
                'Payment Date': new Date(payment.created_at).toLocaleDateString(),
                'Slip URL': payment.slip_url
            }));

            // Create workbook
            const workbook = XLSX.utils.book_new();

            // Add Users sheet
            const usersWS = XLSX.utils.json_to_sheet(usersSheet);
            XLSX.utils.book_append_sheet(workbook, usersWS, 'Users');

            // Add Results sheet
            const resultsWS = XLSX.utils.json_to_sheet(resultsSheet);
            XLSX.utils.book_append_sheet(workbook, resultsWS, 'Results');

            // Add Payments sheet
            const paymentsWS = XLSX.utils.json_to_sheet(paymentsSheet);
            XLSX.utils.book_append_sheet(workbook, paymentsWS, 'Payments');

            // Generate filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `Sudesh_Maths_Complete_Data_${timestamp}.xlsx`;

            // Download file
            XLSX.writeFile(workbook, filename);

            showSuccess('Excel file downloaded successfully!');
        } catch (err) {
            console.error('Export error:', err);
            showError('Failed to export data: ' + err.message);
        } finally {
            // Reset button state
            const exportButton = document.getElementById('export-excel-btn');
            if (exportButton) {
                exportButton.disabled = false;
                exportButton.textContent = 'Download Excel';
            }
        }
    };

    if (showPinModal) {
        return (
            <div className="fixed inset-0 bg-gray-100 flex items-center justify-center z-50">
                <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-sm text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
                        <FaKey size={24} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Security Check</h2>
                    <p className="text-sm text-gray-500 mb-6">Enter admin PIN to access User Management</p>
                    <form onSubmit={handlePinSubmit}>
                        <input
                            type="password"
                            className="w-full text-center text-2xl tracking-widest border rounded-lg p-3 mb-4 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            placeholder="• • • •"
                            maxLength={4}
                            value={pin}
                            onChange={(e) => { setPin(e.target.value); setPinError(''); }}
                            autoFocus
                        />
                        {pinError && <p className="text-red-500 text-sm mb-4">{pinError}</p>}
                        <button type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 transition">
                            Unlock Access
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in-up">
            {/* Header Section */}
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        User Management
                    </h1>
                    <p className="text-gray-500 font-medium">Manage students, admins, and moderators</p>
                </div>
                <div className="flex gap-3">
                    <button
                        id="export-excel-btn"
                        onClick={handleExportToExcel}
                        className="group bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-200 flex items-center gap-3 transform hover:-translate-y-0.5"
                    >
                        <div className="bg-white/20 p-1.5 rounded-lg group-hover:bg-white/30 transition-colors">
                            <FaFileDownload size={14} />
                        </div>
                        <span className="text-sm">Download Excel</span>
                    </button>
                    <button
                        onClick={() => { resetForm(); setShowAddForm(true); }}
                        className="group bg-gray-900 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-gray-800 transition-all shadow-lg shadow-gray-300 flex items-center gap-3 transform hover:-translate-y-0.5"
                    >
                        <div className="bg-white/20 p-1.5 rounded-lg group-hover:bg-white/30 transition-colors">
                            <FaUserPlus size={14} />
                        </div>
                        <span className="text-sm">Add New User</span>
                    </button>
                </div>
            </div>

            {/* Search and Stats Bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <FaSearch className="text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search by name, email, index no..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-11 pr-4 py-3.5 bg-white border-0 rounded-xl shadow-sm ring-1 ring-gray-100 focus:ring-2 focus:ring-blue-500 focus:shadow-md transition-all text-gray-700 font-medium"
                    />
                </div>
                <div className="bg-white px-6 py-3 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                    <span className="text-gray-500 font-medium text-sm">Total Users</span>
                    <span className="text-2xl font-bold text-gray-900">{filteredUsers.length}</span>
                </div>
            </div>

            {showAddForm && (
                <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 md:p-6 overflow-hidden">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowAddForm(false)}></div>

                    {/* Modal Content */}
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-5xl my-auto relative z-10 flex flex-col max-h-[90vh] overflow-hidden animated fadeIn">
                        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0">
                            {/* Modal Header - Fixed */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5 md:px-8 md:py-6 flex justify-between items-center shrink-0">
                                <div>
                                    <h3 className="text-xl md:text-2xl font-bold text-white tracking-tight">{isEditing ? 'Edit User Profile' : 'Create New User'}</h3>
                                    <p className="text-blue-100 text-[10px] md:text-sm mt-0.5">Fill in the details below to {isEditing ? 'update the' : 'add a new'} user account.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(false)}
                                    className="bg-white/10 hover:bg-white/20 text-white rounded-xl p-2 transition-colors focus:outline-none"
                                >
                                    <FaPlus className="rotate-45" size={18} />
                                </button>
                            </div>

                            {/* Modal Body - Scrollable */}
                            <div className="overflow-y-auto flex-grow min-h-0 custom-scrollbar p-6 md:p-8">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Left Column: Account & Personal */}
                                    <div className="space-y-6">
                                        {/* Account Section */}
                                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                                            <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">
                                                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><FaUserPlus size={14} /></div>
                                                Account Details
                                            </h4>
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="col-span-2">
                                                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Full Name</label>
                                                        <input
                                                            name="name"
                                                            placeholder="e.g. John Doe"
                                                            value={newUser.name}
                                                            onChange={handleChange}
                                                            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder:text-gray-300"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-span-2">
                                                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Email Address</label>
                                                        <input
                                                            name="email"
                                                            type="email"
                                                            placeholder="john@example.com"
                                                            value={newUser.email}
                                                            onChange={handleChange}
                                                            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder:text-gray-300"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="col-span-1">
                                                        <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Role</label>
                                                        <select
                                                            name="role"
                                                            value={newUser.role}
                                                            onChange={handleChange}
                                                            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
                                                        >
                                                            <option value="student">Student</option>
                                                            <option value="admin">Admin</option>
                                                            <option value="moderator">Moderator</option>
                                                        </select>
                                                    </div>
                                                    {!isEditing && (
                                                        <div className="col-span-2">
                                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Password</label>
                                                            <input
                                                                name="password"
                                                                type="password"
                                                                placeholder="••••••••"
                                                                value={newUser.password}
                                                                onChange={handleChange}
                                                                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder:text-gray-300"
                                                                required
                                                            />
                                                            <PasswordStrengthIndicator password={newUser.password} showRequirements={true} />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Personal Info Section */}
                                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                                            <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">
                                                <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center"><FaEdit size={14} /></div>
                                                Personal Information
                                            </h4>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Phone Number</label>
                                                    <input name="phone" placeholder="07XXXXXXXX" value={newUser.phone} onChange={handleChange} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all placeholder:text-gray-300" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Birthday</label>
                                                    <input name="birthday" type="date" value={newUser.birthday} onChange={handleChange} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all" />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Address</label>
                                                    <input name="address" placeholder="Residential Address" value={newUser.address} onChange={handleChange} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all placeholder:text-gray-300" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5 ml-1">Age</label>
                                                    <input name="age" type="number" placeholder="18" value={newUser.age} onChange={handleChange} className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all placeholder:text-gray-300" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column: Academic (Only if Student) or Placeholder */}
                                    <div className="space-y-6">
                                        {newUser.role === 'student' ? (
                                            <div className="bg-blue-50/50 rounded-2xl p-6 border border-blue-100 h-full">
                                                <h4 className="flex items-center gap-2 text-sm font-bold text-blue-900 uppercase tracking-wider mb-6">
                                                    <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><FaSearch size={14} /></div>
                                                    Academic Configuration
                                                </h4>

                                                {/* Student Type Selector */}
                                                {!isEditing && (
                                                    <div className="mb-8">
                                                        <label className="block text-xs font-bold text-blue-800 mb-3 ml-1">Student Type (Required for Index)</label>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <label className={`cursor-pointer relative rounded-xl p-4 border-2 transition-all duration-200 ${newUser.student_type === 'physical' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                                                <input
                                                                    type="radio"
                                                                    name="student_type"
                                                                    value="physical"
                                                                    checked={newUser.student_type === 'physical'}
                                                                    onChange={handleChange}
                                                                    className="sr-only"
                                                                />
                                                                <div className="text-center">
                                                                    <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-2 ${newUser.student_type === 'physical' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                                                        <span className="font-bold">P</span>
                                                                    </div>
                                                                    <span className={`block font-bold text-sm ${newUser.student_type === 'physical' ? 'text-blue-700' : 'text-gray-600'}`}>Physical</span>
                                                                    <span className="text-xs text-gray-400">Prefix: SMP</span>
                                                                </div>
                                                            </label>

                                                            <label className={`cursor-pointer relative rounded-xl p-4 border-2 transition-all duration-200 ${newUser.student_type === 'online' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                                                                <input
                                                                    type="radio"
                                                                    name="student_type"
                                                                    value="online"
                                                                    checked={newUser.student_type === 'online'}
                                                                    onChange={handleChange}
                                                                    className="sr-only"
                                                                />
                                                                <div className="text-center">
                                                                    <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center mb-2 ${newUser.student_type === 'online' ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                                                        <span className="font-bold">O</span>
                                                                    </div>
                                                                    <span className={`block font-bold text-sm ${newUser.student_type === 'online' ? 'text-indigo-700' : 'text-gray-600'}`}>Online</span>
                                                                    <span className="text-xs text-gray-400">Prefix: SMO</span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-xs font-semibold text-blue-800/70 mb-1.5 ml-1">Index Number</label>
                                                        <div className="relative">
                                                            <input
                                                                name="index_number"
                                                                placeholder={isEditing ? "Generating..." : "Auto-Generated after save"}
                                                                value={newUser.index_number}
                                                                onChange={handleChange}
                                                                className="w-full pl-4 pr-10 py-3 bg-white/50 border border-blue-200 rounded-xl text-blue-900 placeholder:text-blue-300 font-mono tracking-wider cursor-not-allowed"
                                                                disabled={true}
                                                                title="Index Number is auto-generated"
                                                            />
                                                            <div className="absolute right-3 top-3 text-blue-400">
                                                                <FaKey size={14} />
                                                            </div>
                                                        </div>
                                                        <p className="text-[10px] text-blue-600/60 mt-1 ml-1">
                                                            {isEditing ? 'Index Number cannot be modified.' : 'System will assign the next available number automatically.'}
                                                        </p>
                                                    </div>

                                                    <div className="mb-4">
                                                        <label className="block text-xs font-semibold text-blue-800/70 mb-1.5 ml-1">Medium</label>
                                                        <select
                                                            name="medium"
                                                            value={newUser.medium}
                                                            onChange={handleChange}
                                                            className="w-full px-4 py-2.5 bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all cursor-pointer"
                                                        >
                                                            <option value="Sinhala">Sinhala Medium</option>
                                                            <option value="English">English Medium</option>
                                                        </select>
                                                    </div>

                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-xs font-semibold text-blue-800/70 mb-1.5 ml-1">Grade</label>
                                                            <input
                                                                name="grade"
                                                                placeholder="e.g. 13"
                                                                value={newUser.grade}
                                                                onChange={handleChange}
                                                                className="w-full px-4 py-2.5 bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-xs font-semibold text-blue-800/70 mb-1.5 ml-1">Batch Year</label>
                                                            <input
                                                                name="year"
                                                                placeholder={new Date().getFullYear().toString()}
                                                                value={newUser.year}
                                                                onChange={handleChange}
                                                                className="w-full px-4 py-2.5 bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="h-full border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center text-gray-400 p-8 text-center min-h-[300px]">
                                                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                                    <FaSearch size={24} className="text-gray-300" />
                                                </div>
                                                <p className="font-medium">No additional fields needed</p>
                                                <p className="text-sm mt-1">Academic details are only applicable for users with the <strong>Student</strong> role.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Footer Actions - Fixed */}
                            <div className="px-6 py-4 md:px-8 md:py-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setShowAddForm(false)}
                                    className="px-5 py-2.5 text-gray-700 font-bold hover:bg-gray-100 rounded-xl transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-sm whitespace-nowrap"
                                >
                                    {isEditing ? 'Save Changes' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Users Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-100">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="px-8 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User Profile</th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Role</th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Contact Details</th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Academic Info</th>
                                <th className="px-6 py-5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-50">
                            {currentUsers.length > 0 ? (
                                currentUsers.map((user) => (
                                    <tr key={user.id} className="group hover:bg-blue-50/30 transition-colors duration-200">
                                        <td className="px-8 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10">
                                                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold shadow-md shadow-blue-200 text-lg">
                                                        {user.name.charAt(0)}
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-bold text-gray-900">{user.name}</div>
                                                    <div className="text-xs text-gray-500 font-medium">{user.email}</div>
                                                    {user.role === 'student' && user.index_number && (
                                                        <div className="mt-1">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                                                                📋 {user.index_number}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${user.role === 'admin' ? 'bg-red-50 text-red-700 border-red-100' :
                                                user.role === 'moderator' ? 'bg-yellow-50 text-yellow-700 border-yellow-100' :
                                                    'bg-green-50 text-green-700 border-green-100'
                                                }`}>
                                                {user.role.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 font-medium">{user.phone || '-'}</div>
                                            <div className="text-xs text-gray-500 max-w-[150px] truncate" title={user.address}>{user.address}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {user.role === 'student' ? (
                                                <div className="flex flex-col gap-1">
                                                    {user.index_number ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 w-fit">
                                                            {user.index_number}
                                                        </span>
                                                    ) : <span className="text-xs text-gray-400 italic">No Index</span>}
                                                    <span className="text-xs text-gray-500 font-medium">
                                                        {user.grade ? `Grade ${user.grade}` : ''} {user.year ? `• ${user.year}` : ''}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-gray-300 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                                <button
                                                    onClick={() => openPasswordModal(user)}
                                                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors border border-transparent hover:border-yellow-200"
                                                    title="Change Password"
                                                >
                                                    <FaKey />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(user)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-transparent hover:border-blue-200"
                                                    title="Edit User"
                                                >
                                                    <FaEdit />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                                                    title="Delete User"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-400 text-sm">
                                        No users found matching "{searchQuery}"
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Controls */}
            {
                filteredUsers.length > usersPerPage && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                        <button
                            onClick={() => changePage(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${currentPage === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm'}`}
                        >
                            Previous
                        </button>

                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                key={i}
                                onClick={() => changePage(i + 1)}
                                className={`w-10 h-10 rounded-lg text-sm font-bold flex items-center justify-center transition-all ${currentPage === i + 1
                                    ? 'bg-blue-600 text-white shadow-md transform scale-105'
                                    : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200 shadow-sm'
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}

                        <button
                            onClick={() => changePage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${currentPage === totalPages
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm'}`}
                        >
                            Next
                        </button>
                        <div className="ml-4 text-xs text-gray-500 font-medium whitespace-nowrap hidden sm:block">
                            Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length}
                        </div>
                    </div>
                )
            }

            {/* Password Change Modal */}
            {showPasswordModal && (
                <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-hidden">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPasswordModal(false)}></div>

                    {/* Modal Content */}
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md my-auto relative z-10 animated fadeIn border border-gray-100 flex flex-col max-h-[90vh] overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center shrink-0">
                            <h3 className="text-lg font-bold text-gray-900 leading-tight">Change Password for {selectedUserForPassword?.name}</h3>
                            <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600 p-2">
                                <FaPlus size={16} className="rotate-45" />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto flex-grow min-h-0 custom-scrollbar">
                            <form onSubmit={handlePasswordUpdate} className="space-y-6">
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2">New Password</label>
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="w-full border rounded-xl p-3 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                                        placeholder="Min 6 characters"
                                        required
                                        minLength={6}
                                        autoFocus
                                    />
                                    <PasswordStrengthIndicator password={newPassword} showRequirements={true} />
                                </div>
                                <div className="flex justify-end gap-3 pt-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowPasswordModal(false)}
                                        className="px-6 py-2.5 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all active:scale-95"
                                    >
                                        Update Password
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
        </div >
    );
}
